import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ChevronDownIcon, ArrowPathIcon, CurrencyDollarIcon, CalendarIcon } from '@heroicons/react/24/outline';
import recurringTransactionService from '../services/recurringTransactionService';

const RecurringTransactions = () => {
  const [recurringTransactions, setRecurringTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [includeDeposits, setIncludeDeposits] = useState(false);
  const [minOccurrences, setMinOccurrences] = useState(2);
  const [expandedGroups, setExpandedGroups] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    fetchRecurringTransactions();
  }, [includeDeposits, minOccurrences]);

  const fetchRecurringTransactions = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await recurringTransactionService.getRecurringTransactions({
        includeDeposits,
        minOccurrences
      });

      if (response.success && response.data) {
        setRecurringTransactions(response.data.recurringTransactions || []);
      } else {
        setError(response.message || 'Failed to load recurring transactions');
        toast.error(response.message || 'Failed to load recurring transactions');
      }
    } catch (err) {
      setError(err.message || 'Failed to load recurring transactions');
      toast.error(err.message || 'Failed to load recurring transactions');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpandGroup = (key) => {
    setExpandedGroups(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const viewTransaction = (transactionId) => {
    navigate(`/transactions/${transactionId}`);
  };

  const renderTransactionDetails = (transactions) => {
    return (
      <div className="mt-3 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase text-left">Date</th>
              <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase text-left">Description</th>
              <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase text-right">Amount</th>
              <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase text-left">Account</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {transactions.map(transaction => (
              <tr 
                key={transaction._id} 
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => viewTransaction(transaction._id)}
              >
                <td className="px-3 py-2 text-xs text-gray-900">{formatDate(transaction.date)}</td>
                <td className="px-3 py-2 text-xs text-gray-900">{transaction.description}</td>
                <td className="px-3 py-2 text-xs text-right">
                  <span className={transaction.amount >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {formatCurrency(transaction.amount)}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-gray-900">
                  {transaction.bankName && transaction.lastFourDigits 
                    ? `${transaction.bankName} **** ${transaction.lastFourDigits}`
                    : transaction.account?.bankName && transaction.account?.lastFourDigits
                      ? `${transaction.account.bankName} **** ${transaction.account.lastFourDigits}`
                      : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Recurring Transactions</h1>
        <div className="flex space-x-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="includeDeposits"
              checked={includeDeposits}
              onChange={(e) => setIncludeDeposits(e.target.checked)}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded appearance-checkbox"
            />
            <label htmlFor="includeDeposits" className="ml-2 text-sm text-gray-700">
              Include Deposits
            </label>
          </div>
          <div className="flex items-center">
            <label htmlFor="minOccurrences" className="text-sm text-gray-700 mr-2">
              Min Occurrences:
            </label>
            <select
              id="minOccurrences"
              value={minOccurrences}
              onChange={(e) => setMinOccurrences(Number(e.target.value))}
              className="rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm"
            >
              {[2, 3, 4, 5].map(num => (
                <option key={num} value={num}>{num}</option>
              ))}
            </select>
          </div>
          <button
            onClick={fetchRecurringTransactions}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >
            <ArrowPathIcon className="h-4 w-4 mr-1" />
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      ) : recurringTransactions.length === 0 ? (
        <div className="bg-white shadow overflow-hidden rounded-lg">
          <div className="px-4 py-5 sm:p-6 text-center text-gray-500">
            No recurring transactions found. Try adjusting your filters or adding more transactions.
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {recurringTransactions.map((group) => (
            <div key={group.key} className="bg-white shadow overflow-hidden rounded-lg">
              <div 
                className="px-4 py-5 sm:px-6 cursor-pointer hover:bg-gray-50 flex justify-between items-center"
                onClick={() => toggleExpandGroup(group.key)}
              >
                <div className="flex-1">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {group.description}
                  </h3>
                  <div className="mt-1 flex flex-wrap items-center gap-4">
                    <div className="flex items-center text-sm text-gray-500">
                      <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                      <span>{formatCurrency(group.averageAmount)}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      <span>First: {formatDate(group.firstDate)}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <span className="mx-1">•</span>
                      <span>Last: {formatDate(group.lastDate)}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <span className="mx-1">•</span>
                      <span>Occurrences: {group.occurrences}</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <span className="mx-1">•</span>
                      <span className={recurringTransactionService.getConfidenceClass(group.pattern.confidence)}>
                        {recurringTransactionService.formatPatternType(group.pattern)}
                      </span>
                    </div>
                    {group.nextExpectedDate && (
                      <div className="flex items-center text-sm text-blue-600">
                        <span className="mx-1">•</span>
                        <span>Next: {formatDate(group.nextExpectedDate)}</span>
                      </div>
                    )}
                  </div>
                </div>
                <ChevronDownIcon 
                  className={`h-5 w-5 text-gray-400 transition-transform ${expandedGroups[group.key] ? 'transform rotate-180' : ''}`} 
                />
              </div>
              {expandedGroups[group.key] && (
                <div className="border-t border-gray-200 px-4 py-3 sm:px-6">
                  {renderTransactionDetails(group.transactions)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RecurringTransactions; 